import { useEffect, useRef, useState } from "preact/hooks";
import { lang, locale, t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";
import { Avatar } from "../widgets";

/* Chat between users of the company, one channel per extension (prov/chat.ts):
 * the conversations, and the open one. */

function when(sec: number): string {
  if (!sec) return "";
  const d = new Date(sec * 1000);
  const now = new Date();
  const mins = Math.floor((now.getTime() - d.getTime()) / 60000);
  const time = d.toLocaleTimeString(locale.value, { hour: "2-digit", minute: "2-digit" });
  if (mins < 1) return t("time.just_now");
  if (mins < 60) return t("time.min_ago", mins);
  if (d.toDateString() === now.toDateString()) return time;
  return `${d.toLocaleDateString(locale.value, { day: "numeric", month: "short" })}, ${time}`;
}

function Conversations() {
  const [to, setTo] = useState("");
  const start = () => {
    if (!to) return;
    void S.openChatWith(to);
    setTo("");
  };
  return (
    <section class="chat-list" aria-label={t("chat.conversations")}>
      <h1>{t("chat.title")}</h1>
      {S.chatMine.value.length > 1 && (
        <select
          class="mine"
          aria-label={t("chat.shown")}
          value={S.chatFilter.value}
          onChange={(e) => (S.chatFilter.value = (e.target as HTMLSelectElement).value)}
        >
          <option value="">{t("chat.all_mine")}</option>
          {S.chatMine.value.map((a) => (
            <option value={a}>{S.chatName(a) !== S.extOf(a) ? `${S.extOf(a)} · ${S.chatName(a)}` : S.extOf(a)}</option>
          ))}
        </select>
      )}
      <form
        class="row"
        onSubmit={(e) => {
          e.preventDefault();
          start();
        }}
      >
        <input
          type="text"
          inputMode="numeric"
          placeholder={
            S.chatMine.value.length > 1 ? t("chat.extension_from", S.extOf(S.chatFilter.value || S.chatMine.value[0])) : t("chat.extension")
          }
          aria-label={t("chat.write_to")}
          value={to}
          onInput={(e) => setTo((e.target as HTMLInputElement).value.replace(/[^0-9*#]/g, "").slice(0, 16))}
        />
        <button type="submit" class="btn" disabled={!to} aria-label={t("chat.new")} title={t("chat.new")}>
          <Icon name="plus" size={18} />
        </button>
      </form>
      {S.chatPartners.value.length > 0 && (
        <details class="partners" data-testid="partners">
          <summary>{t("chat.partners")}</summary>
          {S.chatPartners.value.map((p) => (
            <div class="group">
              <div class="sub">{p.name}</div>
              {p.entries.map((e) => (
                <button type="button" class="item conv" onClick={() => void S.openChatWithAddress(e.address)}>
                  <Avatar name={e.name} />
                  <span class="grow">{e.name}</span>
                  <span class="sub">{e.extension}</span>
                </button>
              ))}
            </div>
          ))}
        </details>
      )}
      {S.chats.value.filter((c) => !S.chatFilter.value || c.as === S.chatFilter.value).length === 0 ? (
        <p class="empty">{t("chat.none")}</p>
      ) : (
        <div class="list" data-testid="conversations">
          {S.chats.value.filter((c) => !S.chatFilter.value || c.as === S.chatFilter.value).map((c) => {
            const name = S.convName(c);
            const last = c.last;
            return (
              <button
                type="button"
                class="item conv"
                aria-current={c.id === S.chatOpen.value ? "true" : undefined}
                onClick={() => void S.openChat(c.id)}
              >
                <Avatar name={name} />
                <div class="grow">
                  <div class="row between">
                    <span class={`title ${c.unread ? "bold" : ""}`}>{name}</span>
                    <span class="sub">{when(c.last_at)}</span>
                  </div>
                  <div class="row between">
                    <span class="sub ellipsis">
                      {last ? (last.mine ? t("chat.you") : "") + S.chatPreview(last) : ""}
                    </span>
                    {S.chatMine.value.length > 1 && !S.chatFilter.value && <span class="as">{S.extOf(c.as)}</span>}
                    {c.unread > 0 && <span class="count">{c.unread}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

const blobUrls = new WeakMap<Blob, string>();
function blobUrl(b: Blob): string {
  let u = blobUrls.get(b);
  if (!u) blobUrls.set(b, (u = URL.createObjectURL(b)));
  return u;
}

function size(n: number): string {
  const [b, k, m] = lang.value === "fr" ? ["o", "ko", "Mo"] : ["B", "kB", "MB"];
  const num = (v: number) => v.toLocaleString(locale.value, { maximumFractionDigits: 1 });
  return n < 1024 ? `${n} ${b}` : n < 1024 * 1024 ? `${num(n / 1024)} ${k}` : `${num(n / 1024 / 1024)} ${m}`;
}

/* A message's files: pictures shown, the rest as a chip; a click opens the link */
function Files({ files }: { files: S.ChatFile[] }) {
  return (
    <div class="files">
      {files.map((f) => {
        const href = f.url ?? (f.blob ? blobUrl(f.blob) : undefined);
        const picture = /^image\/(png|jpeg|gif|webp)$/.test(f.type) && href;
        return picture ? (
          <a href={href} target="_blank" rel="noopener noreferrer" class="picture" title={f.name}>
            <img src={href} alt={f.name} loading="lazy" />
          </a>
        ) : (
          <a href={href} target="_blank" rel="noopener noreferrer" class="file-chip" download={f.name}>
            <Icon name="file" size={16} />
            <span class="ellipsis">{f.name}</span>
            <span class="sub">{size(f.size)}</span>
          </a>
        );
      })}
    </div>
  );
}

function Thread() {
  const open = S.chatOpen.value;
  const conv = S.chats.value.find((c) => c.id === open);
  const name = conv ? S.convName(conv) : S.chatName(open.slice(4));
  const seen = S.chatSeenUpTo();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const picker = useRef<HTMLInputElement>(null);
  const addFiles = (list: FileList | File[] | null | undefined) => {
    if (!list) return;
    setFiles((cur) => [...cur, ...Array.from(list)].slice(0, 10));
    input.current?.focus();
  };
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const msgs = S.chatMessages.value;

  useEffect(() => end.current?.scrollIntoView({ block: "end" }), [msgs.length, open]);
  useEffect(() => {
    setEditing("");
    setText("");
    setFiles([]);
    input.current?.focus();
  }, [open]);

  const submit = async () => {
    if (editing) {
      if (!text.trim()) return;
      await S.editChat(editing, text);
    } else {
      if (!text.trim() && !files.length) return;
      if (!(await S.sendChat(text, files))) return;
    }
    setEditing("");
    setText("");
    setFiles([]);
  };

  if (!open) return <section class="chat-thread empty-thread"><p class="empty">{t("chat.choose")}</p></section>;
  return (
    <section
      class="chat-thread"
      aria-label={name}
      onDragOver={(e) => {
        if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (S.chatFiles.value) addFiles(e.dataTransfer?.files);
      }}
    >
      <header class="row">
        <button type="button" class="btn link back" aria-label={t("chat.back")} onClick={() => (S.chatOpen.value = "")}>
          ‹
        </button>
        <h2>
          {name}
          {S.chatMine.value.length > 1 && S.chatOpenAs() && <span class="sub"> · {t("chat.as", S.extOf(S.chatOpenAs()))}</span>}
        </h2>
      </header>
      <div class="msgs" data-testid="messages">
        {msgs.map((m) => (
          <div class={`msg ${m.mine ? "mine" : ""} ${m.pending ? "pending" : ""}`} data-id={m.id}>
            <div class="bubble-row">
              {m.mine && !m.deleted && !m.pending && (
                <span class="msg-actions">
                  <button
                    type="button"
                    class="btn link"
                    aria-label={t("chat.edit")}
                    title={t("chat.edit")}
                    onClick={() => {
                      setEditing(m.id);
                      setText(m.body);
                      input.current?.focus();
                    }}
                  >
                    <Icon name="edit" size={16} />
                  </button>
                  <button type="button" class="btn link" aria-label={t("chat.delete")} title={t("chat.delete")} onClick={() => void S.deleteChat(m.id)}>
                    <Icon name="trash" size={16} />
                  </button>
                </span>
              )}
              <div class={`bubble ${m.deleted ? "deleted" : ""}`}>
                {!m.deleted && !!m.attachments?.length && <Files files={m.attachments} />}
                {m.deleted ? t("chat.deleted") : m.body}
              </div>
            </div>
            <div class="meta">
              {m.pending
                ? t("chat.sending")
                : [when(m.created_at), m.edited_at && !m.deleted ? t("chat.edited") : "", m.mine && m.seq <= seen ? t("chat.seen") : ""].filter(Boolean).join(" · ")}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      {files.length > 0 && (
        <div class="chosen" data-testid="chosen-files">
          {files.map((f, i) => (
            <span class="file-chip">
              <span class="ellipsis">{f.name}</span>
              <button type="button" class="btn link" aria-label={t("chat.remove", f.name)} onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                <Icon name="close" size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
      {editing && <div class="sub">{t("chat.editing")}</div>}
      <form
        class="composer row"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {!editing && S.chatFiles.value && (
          <button type="button" class="btn link" aria-label={t("chat.attach")} title={t("chat.attach")} onClick={() => picker.current?.click()}>
            <Icon name="attach" size={20} />
          </button>
        )}
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          data-testid="file-picker"
          onChange={(e) => {
            addFiles((e.target as HTMLInputElement).files);
            (e.target as HTMLInputElement).value = "";
          }}
        />
        <textarea
          ref={input}
          onPaste={(e) => {
            // a picture pasted from the clipboard
            if (S.chatFiles.value && e.clipboardData?.files.length) {
              e.preventDefault();
              addFiles(e.clipboardData.files);
            }
          }}
          rows={1}
          placeholder={t("chat.placeholder")}
          aria-label={t("chat.message")}
          value={text}
          maxLength={4000}
          onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter: a new line
            if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
              e.preventDefault();
              void submit();
            } else if (e.key === "Escape" && editing) {
              setEditing("");
              setText("");
            }
          }}
        />
        <button type="submit" class="btn primary" disabled={!text.trim() && (!files.length || !!editing)} aria-label={t("chat.send")} title={t("chat.send")}>
          <Icon name="send" size={18} />
        </button>
      </form>
    </section>
  );
}

export function Messages() {
  return (
    <div class={`messages ${S.chatOpen.value ? "has-open" : ""}`}>
      <Conversations />
      <Thread />
    </div>
  );
}
